/**
 * „Was nur ihr sagt" im Bericht: der Abgleich der Kernaussagen mit
 * Wettbewerbstexten (Werkzeug compare_claims, _shared/sim/besetzt.ts).
 *
 * Die Nutzlast kommt aus `fuerBericht`: Aussage, Status, eigenes Zitat mit
 * Stelle, Wettbewerbszitate mit Quelle. Mehr zeigt der Abschnitt nicht, und
 * mehr steht auch nicht drin. Quellenadressen kommen aus Kundeneingaben und
 * gehen deshalb durch `sichererLink` wie jede andere Quelle im Bericht.
 *
 * Reihenfolge: frei zuerst, weil das die Aussagen sind, auf denen die Seite
 * stehen kann. Innerhalb eines Status wie im eigenen Text.
 */

import { sichererLink } from './sicherer-link';
import { sprache, t } from './sprache';

export type BesetztStatus = 'frei' | 'umstritten' | 'besetzt';

export interface BesetztAussage {
  aussage: string;
  status: BesetztStatus;
  zitat: string;
  stelle: string | null;
  hero: boolean;
  belege: Array<{ label: string; url: string | null; zitat: string }>;
}

export interface BesetztBericht {
  erstellt: string | null;
  wettbewerber: Array<{ id: string; label: string; url: string | null }>;
  aussagen: BesetztAussage[];
}

const RANG: Record<BesetztStatus, number> = { frei: 0, umstritten: 1, besetzt: 2 };

export function sortiere(aussagen: BesetztAussage[]): BesetztAussage[] {
  return aussagen.map((a, i) => ({ a, i })).sort((x, y) => RANG[x.a.status] - RANG[y.a.status] || x.i - y.i).map(x => x.a);
}

const ZAHL = ['keine', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht'];
const ZAHL_EN = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];
const zahlwort = (n: number) => (sprache() === 'en' ? ZAHL_EN : ZAHL)[n] ?? String(n);

/** Die Überschrift des Abschnitts: wie viele Aussagen nur diese Seite macht. */
export function besetztTitel(b: BesetztBericht): string {
  const gesamt = b.aussagen.length;
  const frei = b.aussagen.filter(a => a.status === 'frei').length;
  if (sprache() === 'en') {
    if (!frei) return gesamt === 1 ? 'The one core claim also appears at a competitor.' : `Each of the ${zahlwort(gesamt)} core claims also appears at a competitor.`;
    const wort = zahlwort(frei);
    return `${wort.charAt(0).toUpperCase()}${wort.slice(1)} of ${zahlwort(gesamt)} core claims ${frei === 1 ? 'is' : 'are'} made by no competitor.`;
  }
  if (!frei) return `Jede der ${zahlwort(gesamt)} Kernaussagen steht auch bei einem Wettbewerber.`;
  const wort = zahlwort(frei);
  return `${wort.charAt(0).toUpperCase()}${wort.slice(1)} von ${zahlwort(gesamt)} Kernaussagen macht kein Wettbewerber.`;
}

const STATUS_TEXT: Record<BesetztStatus, string> = { frei: 'Frei', umstritten: 'Umstritten', besetzt: 'Besetzt' };
const STATUS_TEXT_EN: Record<BesetztStatus, string> = { frei: 'Free', umstritten: 'Contested', besetzt: 'Taken' };

const ROLLE_EN: Record<string, string> = {
  Headline: 'headline', Subline: 'subline', 'Zwischenüberschrift': 'subheading', Handlungsaufforderung: 'call to action',
  'Fließtext': 'body copy', Beleg: 'proof', Kleingedrucktes: 'small print',
};

/**
 * Die Stelle kommt vom Server auf Deutsch (`stelleText` in besetzt.ts, festes
 * Muster). Auf Englisch wird das Muster übersetzt; was nicht passt, bleibt.
 */
export function stelleEn(stelle: string): string {
  const rolle = (r: string) => ROLLE_EN[r] ?? r;
  let m = /^im Hero \((.+)\)$/.exec(stelle);
  if (m) return `in the hero (${rolle(m[1]!)})`;
  m = /^weiter unten: Abschnitt (\d+) von (\d+) \((.+)\)$/.exec(stelle);
  if (m) return `further down: section ${m[1]} of ${m[2]} (${rolle(m[3]!)})`;
  if (stelle.startsWith('Stelle unbekannt')) return 'location unknown (the text has no recognizable structure)';
  return stelle;
}
const STATUS_FARBE: Record<BesetztStatus, string> = {
  frei: 'border-[#2d6a3e]/30 bg-[#2d6a3e]/[0.07] text-[#2d6a3e]',
  umstritten: 'border-[#d07a3e]/40 bg-[#d07a3e]/[0.08] text-[#8a4a18]',
  besetzt: 'border-[#5f646b]/30 bg-[#5f646b]/[0.07] text-[#4f4841]',
};

const KOPF = "font-serif font-semibold tracking-[-0.015em] text-[#1d1f26]";

export function BesetztListe({ b }: { b: BesetztBericht }) {
  const liste = sortiere(b.aussagen);
  const namen = b.wettbewerber.map(w => w.label).join(', ');
  return (
    <>
      <ol className="m-0 list-none p-0">
        {liste.map((a, i) => (
          <li key={i} className="border-t border-[#1d1f26]/10 py-5 first:border-t-0 break-inside-avoid">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[0.75rem] font-semibold ${STATUS_FARBE[a.status]}`}>{t(STATUS_TEXT, STATUS_TEXT_EN)[a.status]}</span>
              <span className={`${KOPF} text-[1.1875rem] leading-snug`}>{a.aussage}</span>
            </div>
            <p className="mt-2 mb-0 max-w-[62ch] text-[0.9375rem] leading-relaxed text-[#2f3238]">
              <span className="italic font-serif">„{a.zitat}“</span>
              {a.stelle && (
                <span className={`ml-2 text-[0.8125rem] ${a.status === 'frei' && !a.hero ? 'font-semibold text-[#174f4b]' : 'text-[#5f646b]'}`}>
                  {t(<>steht {a.stelle}{a.status === 'frei' && !a.hero ? ', gehört nach oben' : ''}</>,
                    <>appears {stelleEn(a.stelle)}{a.status === 'frei' && !a.hero ? ', belongs at the top' : ''}</>)}
                </span>
              )}
            </p>
            {a.belege.length > 0 && (
              <ul className="mt-2 mb-0 list-none space-y-1 p-0">
                {a.belege.map((w, j) => {
                  const url = sichererLink(w.url);
                  return (
                    <li key={j} className="text-[0.875rem] leading-snug text-[#5f646b]">
                      {url
                        ? <a className="text-[#1f6964] underline underline-offset-[3px]" href={url} rel="noopener noreferrer nofollow">{w.label}</a>
                        : <span className="font-medium text-[#2f3238]">{w.label}</span>}
                      <span className="ml-2 italic">„{w.zitat}“</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ol>
      <p className="mt-4 mb-0 max-w-[70ch] text-[0.8125rem] leading-relaxed text-[#5f646b]">
        {t(<>
        Verglichen mit {namen}. Gemessen wird, ob dieselbe Aussage in diesen Texten steht,
        wörtlich oder sinngemäß, nicht wie der Markt die Anbieter wahrnimmt. Jedes Zitat steht
        wörtlich im jeweiligen Text; was sich nicht wörtlich finden ließ, ist nicht aufgeführt.
        </>, <>
        Compared with {namen}. We measure whether the same claim appears in these texts,
        word for word or in substance, not how the market perceives the vendors. Every quote appears
        word for word in its text; anything that could not be found word for word is not listed.
        </>)}
      </p>
    </>
  );
}
